/**
 * /accounting/1099 — Vendor 1099-NEC Tracking (Phase 9).
 *
 * Year-to-date payment aggregation per vendor, IRS $600 threshold,
 * W-9 status, and blocker count for year-end issuance.
 * Read-only against AP; vendor flags are toggled in-line for admin convenience.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useVendorPO, type W9Status } from "@/data/VendorPOContext";
import { useAP } from "@/data/accounting/ap/APContext";
import {
  build1099Rows, IRS_1099_THRESHOLD, type Vendor1099Row,
} from "@/data/accounting/tax/vendor1099";

const fmt = (n: number) =>
  `$${Math.round(n).toLocaleString()}`;

const W9_LABEL: Record<W9Status, string> = {
  not_requested: "Not requested",
  requested: "Requested",
  on_file: "On file",
  expired: "Expired",
};

const W9_TONE: Record<W9Status, string> = {
  not_requested: "text-muted-foreground",
  requested: "text-amber-600 dark:text-amber-500",
  on_file: "text-emerald-600 dark:text-emerald-500",
  expired: "text-destructive",
};

export default function Vendor1099Page() {
  const { vendors, setVendors } = useVendorPO();
  const { bills, payments } = useAP();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [query, setQuery] = useState("");
  const [onlyEligible, setOnlyEligible] = useState(false);
  const [onlyBlocking, setOnlyBlocking] = useState(false);

  const rows = useMemo<Vendor1099Row[]>(
    () => build1099Rows(vendors, payments, bills, year),
    [vendors, payments, bills, year],
  );

  const filtered = useMemo(() => {
    let r = rows;
    if (onlyEligible) r = r.filter(x => x.is1099Eligible);
    if (onlyBlocking) r = r.filter(x => x.blocking);
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter(x => x.vendorName.toLowerCase().includes(q));
    }
    return r.sort((a, b) => b.ytdPaid - a.ytdPaid);
  }, [rows, query, onlyEligible, onlyBlocking]);

  const totals = useMemo(() => {
    const eligible = rows.filter(r => r.is1099Eligible);
    const reportable = eligible.filter(r => r.meetsThreshold);
    const blocking = eligible.filter(r => r.blocking);
    return {
      eligible: eligible.length,
      reportable: reportable.length,
      reportableTotal: reportable.reduce((s, r) => s + r.ytdPaid, 0),
      blocking: blocking.length,
    };
  }, [rows]);

  const toggleEligible = (vendorId: string, next: boolean) => {
    setVendors(prev => prev.map(v =>
      v.vendorId === vendorId ? { ...v, is1099Eligible: next } : v));
  };

  const setW9 = (vendorId: string, status: W9Status) => {
    setVendors(prev => prev.map(v =>
      v.vendorId === vendorId
        ? {
            ...v,
            w9Status: status,
            w9ReceivedAt: status === "on_file"
              ? (v.w9ReceivedAt ?? new Date().toISOString().slice(0, 10))
              : v.w9ReceivedAt,
          }
        : v));
  };

  const setTaxIdLast4 = (vendorId: string, last4: string) => {
    const clean = last4.replace(/\D/g, "").slice(0, 4);
    setVendors(prev => prev.map(v =>
      v.vendorId === vendorId ? { ...v, taxIdLast4: clean || undefined } : v));
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to General Ledger
        </Link>
        <h1 data-tour-id="vendor-1099-table" className="text-2xl font-semibold tracking-tight">1099-NEC Vendor Tracking</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Calendar year {year} · IRS reporting threshold ${IRS_1099_THRESHOLD}
        </p>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Marked eligible</div>
            <div className="text-2xl font-semibold mt-1">{totals.eligible}</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Reportable (≥ $600)</div>
            <div className="text-2xl font-semibold mt-1">{totals.reportable}</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Reportable $ total</div>
            <div className="text-2xl font-semibold mt-1">{fmt(totals.reportableTotal)}</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Missing W-9 (blockers)</div>
            <div className={`text-2xl font-semibold mt-1 ${totals.blocking > 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-500"}`}>
              {totals.blocking}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search vendor…"
            className="h-8 w-56"
          />
          <select
            value={year}
            onChange={e => setYear(parseInt(e.target.value, 10))}
            className="h-8 text-xs rounded-md border border-border bg-background px-2"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button
            variant={onlyEligible ? "default" : "outline"}
            size="sm"
            onClick={() => setOnlyEligible(!onlyEligible)}
          >
            Eligible only
          </Button>
          <Button
            variant={onlyBlocking ? "default" : "outline"}
            size="sm"
            onClick={() => setOnlyBlocking(!onlyBlocking)}
          >
            Blockers only
          </Button>
          {totals.blocking > 0 && (
            <span className="ml-auto text-xs text-destructive flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {totals.blocking} vendor{totals.blocking === 1 ? "" : "s"} need a W-9 before year-end
            </span>
          )}
        </div>

        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-center px-2 py-2">1099?</th>
                <th className="text-center px-2 py-2">W-9</th>
                <th className="text-center px-2 py-2">TIN last 4</th>
                <th className="text-right px-3 py-2">YTD paid</th>
                <th className="text-right px-3 py-2">Payments</th>
                <th className="text-center px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted-foreground py-8 text-xs">
                    No vendors match the current filters.
                  </td>
                </tr>
              )}
              {filtered.map(r => (
                <tr key={r.vendorId} className="border-t border-border hover:bg-accent/30">
                  <td className="px-3 py-2 font-medium">{r.vendorName}</td>
                  <td className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={r.is1099Eligible}
                      onChange={e => toggleEligible(r.vendorId, e.target.checked)}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <select
                      value={r.w9Status}
                      onChange={e => setW9(r.vendorId, e.target.value as W9Status)}
                      className={`h-7 text-xs rounded-sm border border-border bg-background px-1.5 ${W9_TONE[r.w9Status]}`}
                    >
                      {(Object.keys(W9_LABEL) as W9Status[]).map(k => (
                        <option key={k} value={k}>{W9_LABEL[k]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <Input
                      value={r.taxIdLast4 ?? ""}
                      onChange={e => setTaxIdLast4(r.vendorId, e.target.value)}
                      placeholder="––––"
                      maxLength={4}
                      className="h-7 w-16 text-xs text-center font-mono mx-auto"
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(r.ytdPaid)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.paymentCount}</td>
                  <td className="px-2 py-2 text-center">
                    {!r.is1099Eligible ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : r.blocking ? (
                      <span className="inline-flex items-center gap-1 text-xs text-destructive">
                        <AlertTriangle className="h-3 w-3" /> Missing W-9
                      </span>
                    ) : r.meetsThreshold ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-500">
                        <CheckCircle2 className="h-3 w-3" /> Reportable
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Below $600</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
          The IRS 1099-NEC threshold is $600 in non-employee compensation paid in a calendar year.
          Corporations are generally exempt — leave them unchecked. Always collect a Form W-9 before
          year-end; the &ldquo;Missing W-9&rdquo; flag blocks issuance until on file.
        </p>
      </CockpitSection>
    </CockpitPage>
  );
}
